import { useTranslation } from 'react-i18next';

import type { EstadoPie } from '#components/mobile/transactions/pieEditor';

import type { EtiquetaAyuda, PasoAyuda, PreguntaAyuda } from './pasos';

function uno(selector: string): Element[] {
  const el = document.querySelector(selector);
  return el ? [el] : [];
}

/** Rótulos de latón sobre las partes de `padre` que estén en pantalla. */
function etiquetasDe(
  padre: ParentNode | null,
  partes: ReadonlyArray<readonly [string, string, boolean]>,
): EtiquetaAyuda[] {
  const etiquetas: EtiquetaAyuda[] = [];
  for (const [selector, texto, debajo] of partes) {
    const el = padre?.querySelector(selector);
    if (el) {
      etiquetas.push({ el, texto, debajo });
    }
  }
  return etiquetas;
}

const TRASPASO = '[data-testid="traspaso"]';
const CATEGORIA_TRASPASO = `${TRASPASO} [data-testid="category-field"]`;
const PARTE = '[data-ayuda="parte"]';
const CONFIRMADO = '[data-testid="fila-confirmado"]';
const NO_INGRESO = '[data-testid="no-contar-ingreso"]';
const PIE_DIVIDIR = '[data-ayuda="pie-dividir"]';

/** Las filas Comercio, Categoría y Cuenta de un gasto o ingreso. */
function filasDatos(): Element[] {
  if (document.querySelector(TRASPASO)) {
    return [];
  }
  return [
    '[data-testid="payee-field"]',
    '[data-testid="category-field"]',
    '[data-testid="account-field"]',
  ].flatMap(uno);
}

/** Lo que el detalle sabe de sí mismo y la pantalla no dice. */
export type CasoDetalle = {
  anadir: boolean;
  dividido: boolean;
  fueraPresupuesto: boolean;
  cuadrado: boolean;
  estado: EstadoPie;
};

/**
 * La ayuda del detalle de un movimiento (maqueta aprobada por Vadik el
 * 27-oct-2026): el recorrido de arriba abajo y seis preguntas. Sirve igual
 * para «Añadir movimiento», que es la misma pantalla. Gavi solo señala:
 * nunca pulsa Dividir, Eliminar, Guardar, los interruptores ni el teclado,
 * porque todos escriben.
 */
export function usePreguntasDetalleMovimiento({
  anadir,
  dividido,
  fueraPresupuesto,
  cuadrado,
  estado,
}: CasoDetalle): PreguntaAyuda[] {
  const { t } = useTranslation();

  const textoPieDividir = t(
    'The parts do not add up to the total: this button says how much is left and adds another part, or takes you to the empty one. Until it adds up there is no "Save".',
  );
  const textoTraspasoCategoria = () =>
    document.querySelector(`${TRASPASO} [data-testid="listo-para-asignar"]`)
      ? t(
          'It comes from an off-budget account, so for your Plan it is new money: it goes into "Ready to Assign" and you share it out.',
        )
      : t(
          'It goes to an off-budget account: for your Plan that money leaves, so you can give it a category, like an expense. It is not compulsory.',
        );
  const textoNoIngreso = () =>
    document.querySelector(TRASPASO)
      ? t(
          'Here it comes on and locked: a transfer between your accounts never counts as income in Reports, even if it goes into Ready to Assign.',
        )
      : t(
          '"Don\'t count as income" is for money that comes in but is not your income: a loan, what a friend pays you back. It still adds to Ready to Assign; it only leaves Reports.',
        );
  const textoCuadrado = t(
    'This one is already "Reconciled": you compared it with the bank when reconciling the account and it is locked. If you change it or delete it, I ask you first.',
  );

  const todo: PasoAyuda[] = [
    {
      id: 'hola',
      escena: 'normal',
      texto: anadir
        ? t(
            'Hi! Here you note a transaction: how much, where, which category and which account. Yes, a seagull is going to explain a form to you. Stranger things have happened.',
          )
        : t(
            'Hi! This is a transaction from the inside: here you see all of it and change whatever you need. Yes, a seagull is going to explain a form to you. Stranger things have happened.',
          ),
      objetivo: () => uno('[data-ayuda="titulo-detalle"]'),
    },
    {
      id: 'importe',
      escena: 'normal',
      radio: 18,
      texto: dividido
        ? t(
            'At the top, the total amount. In a split transaction the type does not change: its parts set it, further down.',
          )
        : t(
            'At the top, the amount: tap the figure and my keypad comes up. Below you choose what it was: "Expense" goes out, "Income" comes in and "Transfer" changes account. The border takes its colour.',
          ),
      objetivo: () => uno('[data-testid="importe-movimiento"]'),
      etiquetas: () =>
        etiquetasDe(
          document.querySelector('[data-testid="importe-movimiento"]'),
          [
            ['[data-testid="amount-input"]', t('Amount'), false],
            [
              '[data-testid="selector-segmentado"]',
              t('Transaction type'),
              true,
            ],
          ],
        ),
    },
    {
      id: 'datos',
      escena: 'normal',
      radio: 16,
      texto: dividido
        ? t(
            'Payee and Account apply to the whole transaction. The category is not here: it goes in each part, further down.',
          )
        : fueraPresupuesto
          ? t(
              'Payee: who gets paid or pays. Account: where it goes through. Category is greyed out: this account is off budget and its money is not shared out in the Plan.',
            )
          : anadir
            ? t(
                'Tap each row and choose: the Payee (who gets paid or pays), the Category (which part of your Plan it comes from) and the Account (where the money goes through).',
              )
            : t(
                'Payee: who gets paid or pays. Category: which part of your Plan it comes from. Account: where the money goes through. Tap a row to choose something else.',
              ),
      objetivo: filasDatos,
      etiquetas: () =>
        filasDatos().flatMap((el, i) => {
          const id = el.getAttribute('data-testid');
          const texto =
            id === 'payee-field'
              ? t('Payee')
              : id === 'category-field'
                ? t('Category')
                : t('Account');
          return [{ el, texto, debajo: i > 0 }];
        }),
    },
    {
      id: 'traspaso',
      escena: 'normal',
      radio: 16,
      texto: t(
        'A transfer has no payee: it has "From" and "To", two accounts of yours. The round button swaps the direction. It is neither expense nor income: the money only changes place.',
      ),
      objetivo: () => uno(TRASPASO),
      etiquetas: () =>
        etiquetasDe(document.querySelector(TRASPASO), [
          ['[data-testid="traspaso-desde"]', t('From (transfer)'), false],
          ['[data-testid="traspaso-hacia"]', t('To (transfer)'), true],
        ]),
    },
    {
      id: 'traspaso-categoria',
      escena: 'normal',
      radio: 12,
      get texto() {
        return textoTraspasoCategoria();
      },
      objetivo: () => uno(CATEGORIA_TRASPASO),
    },
    {
      id: 'partes',
      escena: 'normal',
      radio: 12,
      texto: t(
        'It is split: each card is a part, with its amount and its category. The parts have to add up to the total; if not, the button below tells you how much is left.',
      ),
      objetivo: () => uno(PARTE),
      etiquetas: () =>
        etiquetasDe(document.querySelector(PARTE), [
          ['[data-testid^="payee-field-"]', t('Payee'), false],
          ['[data-ayuda="parte-importe"]', t('Amount'), false],
          ['[data-testid^="category-field-"]', t('Category'), true],
        ]),
    },
    {
      id: 'fecha',
      escena: 'normal',
      radio: 12,
      texto: t('The Date is the day it happened. Tap it to change it.'),
      objetivo: () => uno('[data-ayuda="fila-fecha"]'),
    },
    {
      id: 'confirmado',
      escena: 'normal',
      radio: 12,
      texto: cuadrado
        ? textoCuadrado
        : t(
            '"Cleared" is your mark for "I can already see this in my bank". In Accounts, what is still to be confirmed shows as "pend." under the balance.',
          ),
      objetivo: () => uno(CONFIRMADO),
    },
    {
      id: 'no-ingreso',
      escena: 'normal',
      radio: 12,
      get texto() {
        return textoNoIngreso();
      },
      objetivo: () => uno(NO_INGRESO),
    },
    {
      id: 'notas',
      escena: 'normal',
      radio: 12,
      texto: t(
        'In Notes you write whatever you like. With # and a word, like #trip, you make a tag you can later find by searching. The # button starts one for you.',
      ),
      objetivo: () => uno('[data-ayuda="fila-notas"]'),
      etiquetas: () =>
        etiquetasDe(document.querySelector('[data-ayuda="fila-notas"]'), [
          ['button', t('Add tag'), false],
        ]),
    },
    {
      id: 'acciones',
      escena: 'normal',
      radio: 16,
      get texto() {
        const hayDividir = !!document.querySelector('[data-ayuda="dividir"]');
        const hayEliminar = !!document.querySelector(
          '[data-ayuda="eliminar-movimiento"]',
        );
        if (hayDividir && hayEliminar) {
          return t(
            '"Split" shares the transaction out in parts, each with its own category: for a receipt that mixes things. "Delete transaction" removes it; I ask you first, because there is no undo.',
          );
        }
        return hayDividir
          ? t(
              '"Split" shares the transaction out in parts, each with its own category and amount: for a receipt that mixes things.',
            )
          : t(
              '"Delete transaction" removes it for good. I ask you first, with the payee and the amount, because there is no undo.',
            );
      },
      objetivo: () =>
        [
          '[data-ayuda="dividir"]',
          '[data-ayuda="eliminar-movimiento"]',
        ].flatMap(uno),
      etiquetas: () =>
        [
          ...uno('[data-ayuda="dividir"]').map(el => ({
            el,
            texto: t('Split'),
          })),
          ...uno('[data-ayuda="eliminar-movimiento"]').map(el => ({
            el,
            texto: t('Delete transaction'),
          })),
        ] satisfies EtiquetaAyuda[],
    },
    {
      id: 'pie',
      escena: 'normal',
      radio: 16,
      get texto() {
        if (document.querySelector(PIE_DIVIDIR)) {
          return textoPieDividir;
        }
        if (estado.avisoSinCategoria) {
          return t(
            'To approve it, the category is missing; it says so above, in amber. "Save" does save it, but it will stay in Review until you give it one.',
          );
        }
        if (estado.accion === 'anadir') {
          return t(
            'At the bottom, "Add transaction" saves it and you go back to where you were. If the button is greyed out, above it says what is missing: the account or the amount.',
          );
        }
        return estado.accion === 'guardarYAprobar'
          ? t(
              '"Save and approve" saves the changes and marks the transaction as reviewed: it leaves Review. Approving only means "I have looked at it".',
            )
          : t(
              'At the bottom, "Save" keeps the changes and takes you back. You can save without a category; in your Plan it stays "Uncategorized" until you give it one.',
            );
      },
      objetivo: () =>
        document.querySelector(PIE_DIVIDIR)
          ? uno(PIE_DIVIDIR)
          : [
              '[data-testid="pie-principal"]',
              '[data-testid="pie-motivo"]',
            ].flatMap(uno),
    },
    {
      id: 'final',
      escena: 'normal',
      radio: 30,
      final: true,
      texto: t(
        'Done! If you forget something, tap the porthole and I will tell you again. Banks leave it in the small print; I put it in a button.',
      ),
      objetivo: () => uno('[data-testid="ojo-de-buey"]'),
    },
  ];
  const paso = (id: string) => todo.filter(p => p.id === id);
  const con = (
    id: string,
    texto: string,
    objetivo: () => Element[],
    extra: Partial<PasoAyuda> = {},
  ): PasoAyuda => ({
    id,
    escena: 'normal',
    radio: 12,
    texto,
    objetivo,
    ...extra,
  });

  const tipo: PasoAyuda[] = [
    con(
      'tipos',
      t(
        '"Expense": it leaves your pocket towards a shop or a person. "Income": it comes in. "Transfer": it moves from one account of yours to another. The sign of the amount changes by itself.',
      ),
      () => uno('[data-testid="selector-segmentado"]'),
      {
        etiquetas: () =>
          etiquetasDe(
            document.querySelector('[data-testid="selector-segmentado"]'),
            [
              ['[data-testid="selector-gasto"]', t('Spending (sign)'), false],
              ['[data-testid="selector-ingreso"]', t('Income (sign)'), true],
              [
                '[data-testid="selector-traspaso"]',
                t('Transfer (type)'),
                false,
              ],
            ],
          ),
      },
    ),
    con(
      'es-traspaso',
      t(
        'If the money went from one account of yours to another (to savings, to pay the card), tap "Transfer" and I ask you for the other account. It does not take from any category.',
      ),
      () =>
        document.querySelector(TRASPASO)
          ? []
          : uno('[data-testid="selector-traspaso"]'),
    ),
    ...paso('traspaso'),
    ...paso('traspaso-categoria'),
    con(
      'lleva-categoria',
      t(
        'An expense or an income takes a category: that way your Plan knows where it comes from. A transfer does not, unless one of the accounts is off budget.',
      ),
      () =>
        document.querySelector(TRASPASO)
          ? []
          : uno('[data-testid="category-field"]'),
    ),
  ];

  const dividir: PasoAyuda[] = [
    con(
      'dividir',
      t(
        'Tap "Split": the transaction opens in two parts, each with its own payee, amount and category. Add as many as you need.',
      ),
      () =>
        document.querySelector(PARTE) ? [] : uno('[data-ayuda="dividir"]'),
      { radio: 16 },
    ),
    {
      ...paso('partes')[0],
      texto: t(
        'Each card is a part: give it an amount and a category. On the keypad of its amount, "Use what is left" fills in what is still to share out.',
      ),
    },
    con('pie-dividir', textoPieDividir, () => uno(PIE_DIVIDIR), { radio: 16 }),
    con(
      'pie-partes',
      t(
        'The parts have to add up to the total; while they do not, this shows how much is left instead of "Save". Once saved, it shows as "Split" in Transactions.',
      ),
      () =>
        document.querySelector(PARTE) && !document.querySelector(PIE_DIVIDIR)
          ? uno('[data-testid="pie-principal"]')
          : [],
      { radio: 16 },
    ),
  ];

  // La categoría de un gasto o ingreso que se puede elegir (no la de un
  // traspaso, ni apagada por estar fuera del presupuesto, ni en partes).
  const categoriaLibre = () =>
    fueraPresupuesto || dividido || document.querySelector(TRASPASO)
      ? []
      : uno('[data-testid="category-field"]');
  const devolucion: PasoAyuda[] = [
    con(
      'devolucion-ingreso',
      t('Choose "Income" and type in what you got back.'),
      () =>
        categoriaLibre().length ? uno('[data-testid="selector-ingreso"]') : [],
    ),
    con(
      'devolucion-categoria',
      t(
        'Give it the same category the purchase had. That way the category gets back what was returned in your Plan, and in Reports it takes off spending: it does not count as new income.',
      ),
      categoriaLibre,
    ),
    con(
      'devolucion-no-ingreso',
      t(
        '"Don\'t count as income" is not needed here: it only works with income categories. With the purchase\'s category it already does not count as income.',
      ),
      () => (categoriaLibre().length ? uno(NO_INGRESO) : []),
    ),
  ];

  const confirmado: PasoAyuda[] = cuadrado
    ? [con('cuadrado', textoCuadrado, () => uno(CONFIRMADO))]
    : [
        con(
          'confirmado-que',
          t(
            '"Cleared" is a mark of yours: "I can already see this one in my bank". It is switched on and off with the toggle; it does not change the balance or the Plan.',
          ),
          () => uno(CONFIRMADO),
        ),
        con(
          'confirmado-para',
          t(
            'It helps you reconcile: in Accounts, what is not cleared shows as "pend." under the balance, and it usually explains why it does not match the bank.',
          ),
          () => uno(CONFIRMADO),
        ),
        con(
          'cuadrado-siguiente',
          t(
            '"Reconciled" is the next step: when you reconcile the account with the bank (in its ⋯), the cleared ones become reconciled and are locked. Changing one asks you to confirm.',
          ),
          () => uno(CONFIRMADO),
        ),
      ];

  const noIngreso: PasoAyuda[] = [
    con(
      'no-ingreso-cuenta',
      t(
        'Everything that comes in with an income category counts as your income in Reports and Home: pay, income versus expenses, what you save.',
      ),
      () => (document.querySelector(TRASPASO) ? [] : uno(NO_INGRESO)),
    ),
    con(
      'no-ingreso-cuando',
      t(
        'Switch it on if that money is not really income: a loan, what a friend pays you back. It leaves Reports, but it still adds to the balance and to Ready to Assign.',
      ),
      () => (document.querySelector(TRASPASO) ? [] : uno(NO_INGRESO)),
    ),
    con(
      'no-ingreso-traspaso',
      t(
        'Here it comes on and locked: a transfer between your accounts never counts as income in Reports, even if it goes into Ready to Assign.',
      ),
      () => (document.querySelector(TRASPASO) ? uno(NO_INGRESO) : []),
    ),
    con(
      'no-ingreso-nota',
      t(
        'I note it as the tag #noingreso in Notes: that way it travels with the transaction. If you delete the tag from the note, it counts again.',
      ),
      () =>
        document.querySelector(NO_INGRESO) && !document.querySelector(TRASPASO)
          ? uno('[data-ayuda="fila-notas"]')
          : [],
    ),
  ];

  // Se señala solo la fila que hay que tocar: el motivo está abajo, en el
  // pie, y unirlos taparía la pantalla entera.
  const bloqueo: PasoAyuda[] = [
    con(
      'falta-cuenta',
      t(
        'The account is missing: without knowing which account it went through I cannot save it. Tap "Account" and choose one.',
      ),
      () =>
        estado.motivo === 'cuenta' ? uno('[data-testid="account-field"]') : [],
    ),
    con(
      'falta-importe',
      t(
        'The amount is missing: a transaction of zero is almost always a slip. Tap the figure and type it in.',
      ),
      () =>
        estado.motivo === 'importe'
          ? uno('[data-testid="importe-movimiento"]')
          : [],
    ),
    con(
      'falta-categoria',
      t(
        'To approve it, it needs a category: tap "Category" and choose one. With "Save" it is saved anyway, but it stays in Review until it has one.',
      ),
      () =>
        !estado.motivo && estado.avisoSinCategoria
          ? uno('[data-testid="category-field"]')
          : [],
    ),
    con('bloqueo-dividir', textoPieDividir, () => uno(PIE_DIVIDIR), {
      radio: 16,
    }),
  ];

  return [
    { id: 'todo', texto: t('Tell me about this screen'), pasos: todo },
    {
      id: 'tipo',
      texto: t('Is it an expense, an income or a transfer?'),
      pasos: tipo,
    },
    {
      id: 'dividir',
      texto: t('How do I split a receipt into several categories?'),
      pasos: dividir,
    },
    {
      id: 'devolucion',
      texto: t('I got money back. How do I note it?'),
      pasos: devolucion,
    },
    {
      id: 'confirmado',
      texto: t('What are "Cleared" and "Reconciled"?'),
      pasos: confirmado,
    },
    {
      id: 'no-ingreso',
      texto: t('What is "Don\'t count as income" for?'),
      pasos: noIngreso,
    },
    {
      id: 'bloqueo',
      texto: t('Why can I not save or approve?'),
      pasos: bloqueo,
    },
  ];
}
